import type { FC, ReactNode } from 'react'
import type { BreadcrumbItem } from '@/components/generals/breadcrumbs'

import { cn } from '@/lib/utils'
import Container from '@/components/generals/container'
import Breadcrumbs from '@/components/generals/breadcrumbs'
import AppImage from '@/components/generals/app-image'
import { Eyebrow, displayTitleClass } from '@/components/generals/section-heading'

interface PageHeroProps {
    eyebrow: ReactNode
    title: ReactNode
    description?: ReactNode
    breadcrumbs: Array<BreadcrumbItem>
    image?: {
        src: string | null
        alt: string | null
    }
    children?: ReactNode
}

const PageHero: FC<PageHeroProps> = ({ eyebrow, title, description, breadcrumbs, image, children }): ReactNode => {
    return (
        <section className='surface-grain relative overflow-hidden bg-black'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-40 right-0 h-96 w-[36rem] rounded-full bg-brass/10 blur-3xl' />

            <Container className='relative pt-10 pb-16 md:pt-14 md:pb-24'>
                <Breadcrumbs items={breadcrumbs} />

                <div className={cn('mt-12 grid items-center gap-12 md:mt-16', image && 'lg:grid-cols-12 lg:gap-16')}>
                    <div className={cn('flex flex-col gap-6', image ? 'lg:col-span-7' : 'max-w-4xl')}>
                        <Eyebrow>{eyebrow}</Eyebrow>

                        <h1 className={cn(displayTitleClass, 'text-[2.6rem] sm:text-6xl lg:text-7xl')}>
                            {title}
                        </h1>

                        {description && (
                            <div className='max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg'>
                                {description}
                            </div>
                        )}

                        {children}
                    </div>

                    {image && (
                        <div className='relative lg:col-span-5'>
                            <div aria-hidden='true' className='absolute -inset-3 rounded-md border border-brass/25 sm:-inset-4' />

                            <div className='relative aspect-[4/5] overflow-hidden rounded-sm bg-graphite'>
                                <AppImage
                                    src={image.src}
                                    alt={image.alt}
                                    sizes='(min-width: 1024px) 40vw, 100vw'
                                    preload
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Container>

            <div className='hairline-brass' />
        </section>
    )
}

export default PageHero
